import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { ChoiceKey, ReviewEntry } from './types';

type ReviewListProps = {
  entries: ReviewEntry[];
};

export function ReviewList({ entries }: ReviewListProps) {
  return (
    <div className="review-list">
      {entries.map((entry, idx) => {
        const correct = entry.is_correct;
        const cardAccent = correct ? 'border-success' : 'border-danger';
        const badgeClass = correct ? 'bg-success' : 'bg-danger';
        return (
          <div key={entry.questionId} className={`card mb-3 shadow-sm ${cardAccent}`}>
            <div className={`card-header d-flex align-items-center ${correct ? 'bg-success-subtle' : 'bg-danger-subtle'}`}>
              <span className={`badge ${badgeClass} me-2`}>{correct ? 'Correct' : 'Review'}</span>
              <h5 className="card-title mb-0">Question {idx + 1}</h5>
            </div>
            <div className="card-body">
              <div className="markdown mb-3">
                <ReactMarkdown remarkPlugins={[remarkGfm as any]}>{entry.prompt}</ReactMarkdown>
              </div>
              <div className="row g-2">
                {(['A', 'B', 'C', 'D'] as ChoiceKey[]).map((choice) => {
                  const isCorrect = entry.correct_choice === choice;
                  const isSelected = entry.student_choice === choice;
                  return (
                    <div key={choice} className="col-12 col-md-6">
                      <div
                        className={`border rounded px-3 py-2 h-100 review-choice-card ${
                          isCorrect ? 'border-success bg-success-subtle' : 'bg-light'
                        } ${isSelected ? 'review-choice-selected' : ''}`}
                      >
                        <span className="fw-bold me-2">{choice}.</span>
                        <span>{entry.choices[choice]}</span>
                        {isSelected && !isCorrect ? (
                          <span className="badge bg-warning-subtle text-warning ms-2">Your answer</span>
                        ) : null}
                        {isCorrect ? (
                          <span className="badge bg-success ms-2">Correct</span>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 d-flex flex-wrap gap-2 align-items-center small text-muted">
                <span className={`fw-semibold ${entry.verified ? 'text-success' : 'text-warning'}`}>
                  {entry.verified ? 'Verified ✅' : 'Not verified'}
                </span>
                {entry.verification_notes ? <span>{entry.verification_notes}</span> : null}
              </div>
              <div className="mt-2 small">
                <p className="mb-1">
                  <strong>Correct answer:</strong> {entry.correct_choice}. {entry.choices[entry.correct_choice]}
                </p>
                <p className="mb-0">
                  <strong>Your answer:</strong>{' '}
                  {entry.student_choice
                    ? `${entry.student_choice}. ${entry.choices[entry.student_choice]}`
                    : 'Not answered'}
                </p>
              </div>
              {entry.recommended_steps && (
                <div className="mt-3">
                  <h6 className="fw-semibold">Recommended steps</h6>
                  <ul className="ps-3 mb-0">
                    {entry.recommended_steps.split(/\n+/).map((step, i) => (
                      <li key={i} className="small mb-1">
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
